import { useEffect, useState } from "react";
import fire5Sprite from '../Moves/fire5.png';
import explosionSprite from '../Moves/PRAS- Explosions.png';
import "./FlameWheelAnimation.css";

/**
 * PROFESSIONAL FLAME WHEEL ANIMATION - REDESIGNED
 * Fire-type move with authentic Pokémon-style 2-phase sequence
 * 
 * Key Features:
 * - Phase 1: Frame 5 from fire5.png rotating around player (power-up)
 * - Phase 2: Explosion sequence using PRAS-Explosions.png frames 0-3
 * - Aftershock: Frame 6 from fire5.png flash effect
 * - Professional gaming company quality with proper timing
 */

export default function FlameWheelAnimation({ playerRef, opponentRef, onComplete }) {
  const [animationPhase, setAnimationPhase] = useState('idle');
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = document.querySelector('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    const pRect = playerEl.getBoundingClientRect();
    const oRect = opponentEl.getBoundingClientRect();
    
    // Target the actual visible sprite with bias correction
    const targetEl = opponentEl.querySelector?.('.wild-front') || opponentEl;
    const targetRect = targetEl.getBoundingClientRect();
    const biasX = Number(targetEl.dataset?.hitCenterBiasX ?? opponentEl.dataset?.hitCenterBiasX ?? -25);
    const biasY = Number(targetEl.dataset?.hitCenterBiasY ?? opponentEl.dataset?.hitCenterBiasY ?? 0);

    const playerCenterX = (pRect.left + pRect.width / 2) - containerRect.left;
    const playerCenterY = (pRect.top + pRect.height / 2) - containerRect.top;
    const opponentCenterX = (targetRect.left + targetRect.width / 2) - containerRect.left + biasX;
    const opponentCenterY = (targetRect.top + targetRect.height / 2) - containerRect.top + biasY;
    
    // SPRITE CONFIGURATIONS
    const FIRE5_CONFIG = {
      frameWidth: 192,
      frameHeight: 192,
      spriteWidth: 960,
      spriteHeight: 576,
      // Frame 5 (index 4): Large intense flame for power-up (row 1, col 5)
      frame5: { x: 768, y: 0 }, // -768px 0px
      // Frame 6 (index 5): Aftershock flame (row 2, col 1)
      frame6: { x: 0, y: 192 } // 0px -192px
    };
    
    const EXPLOSION_CONFIG = {
      frameWidth: 192,
      frameHeight: 192,
      spriteWidth: 768,
      spriteHeight: 576,
      // Explosion frames 0-3 (top row)
      frame0: { x: 0, y: 0 },     // 0px 0px
      frame1: { x: 192, y: 0 },   // -192px 0px
      frame2: { x: 384, y: 0 },   // -384px 0px
      frame3: { x: 576, y: 0 }    // -576px 0px
    };

    const cleanupElements = [];
    const cleanupTimers = [];
    
    console.log('🔥 Flame Wheel Animation Starting - Professional Design!');
    
    // PHASE 1: POWER-UP - Flames rotate around player (0-1000ms)
    const powerUpTimer = setTimeout(() => {
      setAnimationPhase('powerup');
      console.log('🔥 Phase 1: Power-Up - Flames rotating around player');
      
      // Add charging effect to player
      if (playerEl) {
        playerEl.classList.add('flame-wheel-charge');
      }
      
      // Create 8 flame sprites around player using frame 5
      const flameCount = 8;
      const radius = 50; // Distance from player center
      
      for (let i = 0; i < flameCount; i++) {
        const angle = (i / flameCount) * Math.PI * 2;
        const angleDeg = Math.round((angle * 180) / Math.PI);
        
        setTimeout(() => {
          const flame = document.createElement('div');
          flame.className = 'flame-wheel-powerup';
          flame.style.left = `${playerCenterX}px`;
          flame.style.top = `${playerCenterY}px`;
          flame.style.width = `${FIRE5_CONFIG.frameWidth}px`;
          flame.style.height = `${FIRE5_CONFIG.frameHeight}px`;
          flame.style.backgroundImage = `url(${fire5Sprite})`;
          flame.style.backgroundSize = `${FIRE5_CONFIG.spriteWidth}px ${FIRE5_CONFIG.spriteHeight}px`;
          flame.style.backgroundPosition = `-${FIRE5_CONFIG.frame5.x}px -${FIRE5_CONFIG.frame5.y}px`;
          
          // Set CSS variables for rotation
          flame.style.setProperty('--angle-start', `${angleDeg}deg`);
          flame.style.setProperty('--radius', `${radius}px`);
          flame.style.setProperty('--rotation-delay', `${i * 0.1}s`);
          
          container.appendChild(flame);
          cleanupElements.push(flame);
          
          console.log(`🔥 Power-up Flame ${i + 1} created at angle ${angleDeg}°`);
        }, i * 60); // Staggered appearance
      }
      
      // Create projectile orb that will launch toward opponent
      setTimeout(() => {
        const orb = document.createElement('div');
        orb.className = 'flame-wheel-orb';
        orb.style.left = `${playerCenterX}px`;
        orb.style.top = `${playerCenterY}px`;
        orb.style.setProperty('--target-x', `${opponentCenterX - playerCenterX}px`);
        orb.style.setProperty('--target-y', `${opponentCenterY - playerCenterY}px`);
        
        container.appendChild(orb);
        cleanupElements.push(orb);
        
        console.log('🔥 Flame orb created and launching');
      }, 600);
      
    }, 0);
    cleanupTimers.push(powerUpTimer);

    // PHASE 2: IMPACT & EXPLOSION - Multi-frame explosion sequence (1000-1800ms)
    const impactTimer = setTimeout(() => {
      setAnimationPhase('impact');
      console.log('🔥 Phase 2: Impact - Explosion sequence');
      
      // Remove power-up flames
      const powerupFlames = container.querySelectorAll('.flame-wheel-powerup');
      powerupFlames.forEach(flame => {
        flame.style.animation = 'flameWheelFadeOut 0.2s ease-out forwards';
        setTimeout(() => flame.remove(), 200);
      });
      
      // Create explosion sequence using PRAS-Explosions.png frames 0-3
      const explosionFrames = [0, 1, 2, 3];
      let frameIndex = 0;
      
      const createExplosionFrame = () => {
        if (frameIndex < explosionFrames.length) {
          const currentFrame = explosionFrames[frameIndex];
          const frameKey = `frame${currentFrame}`;
          const frame = EXPLOSION_CONFIG[frameKey];
          
          const explosion = document.createElement('div');
          explosion.className = 'flame-wheel-explosion';
          explosion.style.left = `${opponentCenterX}px`;
          explosion.style.top = `${opponentCenterY}px`;
          explosion.style.width = `${EXPLOSION_CONFIG.frameWidth}px`;
          explosion.style.height = `${EXPLOSION_CONFIG.frameHeight}px`;
          explosion.style.backgroundImage = `url(${explosionSprite})`;
          explosion.style.backgroundSize = `${EXPLOSION_CONFIG.spriteWidth}px ${EXPLOSION_CONFIG.spriteHeight}px`;
          explosion.style.backgroundPosition = `-${frame.x}px -${frame.y}px`;
          explosion.style.animation = 'flameWheelExplosionFrame 0.15s ease-out forwards';
          
          container.appendChild(explosion);
          cleanupElements.push(explosion);
          
          console.log(`🔥 Explosion Frame ${currentFrame} (${frameIndex + 1}/${explosionFrames.length})`);
          
          // Remove previous explosion frame
          if (frameIndex > 0) {
            const prevExplosions = container.querySelectorAll('.flame-wheel-explosion');
            if (prevExplosions.length > 1) {
              prevExplosions[0].remove();
            }
          }
          
          frameIndex++;
          setTimeout(createExplosionFrame, 150);
        } else {
          // Add aftershock flash using frame 6
          setTimeout(() => {
            const aftershock = document.createElement('div');
            aftershock.className = 'flame-wheel-aftershock';
            aftershock.style.left = `${opponentCenterX}px`;
            aftershock.style.top = `${opponentCenterY}px`;
            aftershock.style.width = `${FIRE5_CONFIG.frameWidth}px`;
            aftershock.style.height = `${FIRE5_CONFIG.frameHeight}px`;
            aftershock.style.backgroundImage = `url(${fire5Sprite})`;
            aftershock.style.backgroundSize = `${FIRE5_CONFIG.spriteWidth}px ${FIRE5_CONFIG.spriteHeight}px`;
            aftershock.style.backgroundPosition = `-${FIRE5_CONFIG.frame6.x}px -${FIRE5_CONFIG.frame6.y}px`;
            aftershock.style.animation = 'flameWheelAftershock 0.4s ease-out forwards';
            
            container.appendChild(aftershock);
            cleanupElements.push(aftershock);
            
            console.log('🔥 Aftershock flash effect');
          }, 100);
        }
      };
      
      createExplosionFrame();
      
    }, 1000);
    cleanupTimers.push(impactTimer);

    // Opponent hit reaction and screen effects
    setTimeout(() => {
      if (opponentEl) {
        opponentEl.classList.add('flame-wheel-hit');
        setTimeout(() => {
          opponentEl.classList.remove('flame-wheel-hit');
        }, 400);
      }
      
      // Screen flash effect
      const screenFlash = document.createElement('div');
      screenFlash.className = 'flame-wheel-screen-flash';
      screenFlash.style.position = 'fixed';
      screenFlash.style.top = '0';
      screenFlash.style.left = '0';
      screenFlash.style.width = '100vw';
      screenFlash.style.height = '100vh';
      screenFlash.style.background = 'rgba(255, 120, 0, 0.3)';
      screenFlash.style.pointerEvents = 'none';
      screenFlash.style.zIndex = '1000';
      screenFlash.style.animation = 'flameWheelScreenFlash 0.3s ease-out';
      container.appendChild(screenFlash);
      cleanupElements.push(screenFlash);
      
      // Complete animation
      setTimeout(() => {
        console.log('🔥 Flame Wheel Animation Complete - Professional Design!');
        onComplete();
      }, 500);
    }, 1200);

    // Cleanup function
    return () => {
      // Remove all classes from player and opponent
      if (playerEl) {
        playerEl.classList.remove('flame-wheel-charge');
      }
      if (opponentEl) {
        opponentEl.classList.remove('flame-wheel-hit');
      }
      
      // Remove all created elements
      cleanupElements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Clear all timers
      cleanupTimers.forEach(timer => clearTimeout(timer));
      
      console.log('🔥 Flame Wheel Animation Cleanup Complete');
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

FlameWheelAnimation.moveType = "fire";
